import * as React from 'react';
import {cn} from '../../lib/utils';

// Typography components following shadcn/ui patterns
const TypographyH1 = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({className, ...props}, ref) => (
  <h1
    ref={ref}
    className={cn(
      'scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl',
      className
    )}
    {...props}
  />
));
TypographyH1.displayName = 'TypographyH1';

const TypographyH2 = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({className, ...props}, ref) => (
  <h2
    ref={ref}
    className={cn(
      'scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight first:mt-0',
      className
    )}
    {...props}
  />
));
TypographyH2.displayName = 'TypographyH2';

const TypographyH3 = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({className, ...props}, ref) => (
  <h3
    ref={ref}
    className={cn(
      'scroll-m-20 text-2xl font-semibold tracking-tight',
      className
    )}
    {...props}
  />
));
TypographyH3.displayName = 'TypographyH3';

const TypographyH4 = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({className, ...props}, ref) => (
  <h4
    ref={ref}
    className={cn(
      'scroll-m-20 text-xl font-semibold tracking-tight',
      className
    )}
    {...props}
  />
));
TypographyH4.displayName = 'TypographyH4';

const TypographyP = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({className, ...props}, ref) => (
  <p
    ref={ref}
    className={cn('leading-7 [&:not(:first-child)]:mt-6', className)}
    {...props}
  />
));
TypographyP.displayName = 'TypographyP';

const TypographyBlockquote = React.forwardRef<
  HTMLQuoteElement,
  React.HTMLAttributes<HTMLQuoteElement>
>(({className, ...props}, ref) => (
  <blockquote
    ref={ref}
    className={cn('mt-6 border-l-2 pl-6 italic', className)}
    {...props}
  />
));
TypographyBlockquote.displayName = 'TypographyBlockquote';

const TypographyTable = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({className, ...props}, ref) => (
  <div className="my-6 w-full overflow-y-auto">
    <table ref={ref} className={cn('w-full', className)} {...props} />
  </div>
));
TypographyTable.displayName = 'TypographyTable';

const TypographyList = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLUListElement>
>(({className, ...props}, ref) => (
  <ul
    ref={ref}
    className={cn('my-6 ml-6 list-disc [&>li]:mt-2', className)}
    {...props}
  />
));
TypographyList.displayName = 'TypographyList';

const TypographyInlineCode = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement>
>(({className, ...props}, ref) => (
  <code
    ref={ref}
    className={cn(
      'bg-muted relative rounded px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold',
      className
    )}
    {...props}
  />
));
TypographyInlineCode.displayName = 'TypographyInlineCode';

const TypographyLead = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({className, ...props}, ref) => (
  <p
    ref={ref}
    className={cn('text-muted-foreground text-xl', className)}
    {...props}
  />
));
TypographyLead.displayName = 'TypographyLead';

const TypographyLarge = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({className, ...props}, ref) => (
  <div
    ref={ref}
    className={cn('text-lg font-semibold', className)}
    {...props}
  />
));
TypographyLarge.displayName = 'TypographyLarge';

const TypographySmall = React.forwardRef<
  HTMLElement,
  React.HTMLAttributes<HTMLElement>
>(({className, ...props}, ref) => (
  <small
    ref={ref}
    className={cn('text-sm leading-none font-medium', className)}
    {...props}
  />
));
TypographySmall.displayName = 'TypographySmall';

const TypographyMuted = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({className, ...props}, ref) => (
  <p
    ref={ref}
    className={cn('text-muted-foreground text-sm', className)}
    {...props}
  />
));
TypographyMuted.displayName = 'TypographyMuted';

// Legacy Typography component for backward compatibility
export interface LegacyTypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'body1'
    | 'body2'
    | 'subtitle1'
    | 'subtitle2';
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div';
  textAlign?: 'left' | 'center' | 'right' | 'justify';
}

const Typography = React.forwardRef<HTMLElement, LegacyTypographyProps>(
  ({variant = 'body1', as, className, children, textAlign, ...props}, ref) => {
    const Component = as || getDefaultElement(variant);

    const variantClasses = {
      h1: 'scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl',
      h2: 'scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight first:mt-0',
      h3: 'scroll-m-20 text-2xl font-semibold tracking-tight',
      h4: 'scroll-m-20 text-xl font-semibold tracking-tight',
      h5: 'text-lg font-semibold',
      h6: 'text-base font-semibold',
      body1: 'text-xs leading-[20px]',
      body2: 'text-sm leading-[22px]',
      subtitle1: 'text-xl text-muted-foreground',
      subtitle2: 'text-base text-muted-foreground'
    };

    const elementProps = {
      ref,
      className: cn(variantClasses[variant], className),
      style: {
        ...(textAlign && {textAlign})
      },
      ...props
    };

    return React.createElement(Component as any, elementProps, children);
  }
);

Typography.displayName = 'Typography';

function getDefaultElement(variant: string): string {
  switch (variant) {
    case 'h1':
      return 'h1';
    case 'h2':
      return 'h2';
    case 'h3':
      return 'h3';
    case 'h4':
      return 'h4';
    case 'h5':
      return 'h5';
    case 'h6':
      return 'h6';
    case 'subtitle1':
    case 'subtitle2':
      return 'p';
    case 'body1':
    case 'body2':
    default:
      return 'p';
  }
}

export {
  Typography,
  TypographyH1,
  TypographyH2,
  TypographyH3,
  TypographyH4,
  TypographyP,
  TypographyBlockquote,
  TypographyTable,
  TypographyList,
  TypographyInlineCode,
  TypographyLead,
  TypographyLarge,
  TypographySmall,
  TypographyMuted
};
